import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronDown, CirclePlay, Copy, Pencil, Route, Sparkles, SquareCheck, UserPlus, X, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { S3Modal } from '../v3/primitives';
import { NOVITA_VERSIONE, NovitaArea, NovitaIcona, NovitaVoce } from './supportNovita';
// Condivisa con l'area rappresentante: i fogli scoped vanno importati qui (come InviteParticipantModal).
import '../../../styles/admin-v2.css';
import '../../../styles/support-v3.css';
import '../../../styles/support-novita.css';

export const NOVITA_VIDEO_URL = 'https://youtu.be/wCZ_UHG2NHg';

const ICONE: Record<NovitaIcona, LucideIcon> = {
  zap: Zap,
  route: Route,
  'check-square': SquareCheck,
  'user-plus': UserPlus,
  copy: Copy,
  pencil: Pencil,
};

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

/** '2026-10-15' → '15 ottobre 2026'; una versione non in forma di data resta com'è. */
export function dataVersione(versione: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(versione);
  if (!m) return versione;
  const mese = MESI[Number(m[2]) - 1];
  return mese ? `${Number(m[3])} ${mese} ${m[1]}` : versione;
}

export interface NovitaModalProps {
  open: boolean;
  voci: NovitaVoce[];
  area: NovitaArea;
  /** true = «Non mostrare più» spuntato e confermato con «Ho capito»; X/Esc = false. */
  onClose: (nonMostrarePiu: boolean) => void;
  versione?: string;
}

/**
 * Popup «Novità del Centro Ticket»: elenco di voci espandibili. Stesso markup
 * nelle due vesti (S3Modal porta `.adm2 .sup3` e, in v4, `sup4`).
 */
const NovitaModal: React.FC<NovitaModalProps> = ({ open, voci, area, onClose, versione = NOVITA_VERSIONE }) => {
  const [aperte, setAperte] = useState<Set<string>>(() => new Set());
  const [nonMostrare, setNonMostrare] = useState(false);

  // Ogni apertura riparte da voci compresse e spunta tolta.
  useEffect(() => {
    if (open) {
      setAperte(new Set());
      setNonMostrare(false);
    }
  }, [open]);

  const tutteAperte = voci.length > 0 && voci.every((v) => aperte.has(v.id));
  const toggle = (id: string) => setAperte((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleTutte = () => setAperte(tutteAperte ? new Set() : new Set(voci.map((v) => v.id)));
  const guida = area === 'rep' ? '/area-associativa/guida-ticket' : '/admin/guida-ticket';

  return (
    <S3Modal open={open} onClose={() => onClose(false)} labelledBy="s3-novita-title">
      <div className="s3-mod s3-nov">
        <div className="s3-nov__hero">
          <div className="s3-nov__hero-top">
            <span className="s3-nov__pill"><Sparkles size={11} aria-hidden="true" />NOVITÀ</span>
            <span className="s3-nov__date">{dataVersione(versione)}</span>
            <button type="button" className="s3-nov__close" onClick={() => onClose(false)} aria-label="Chiudi">
              <X size={15} />
            </button>
          </div>
          <h3 className="s3-nov__title" id="s3-novita-title">Novità del Centro Ticket</h3>
          <p className="s3-nov__lead">
            Abbiamo aggiunto alcune funzioni per lavorare meglio sui ticket. Apri una voce per leggere come funziona, passo passo.
          </p>
        </div>

        <div className="s3-mod__body s3-nov__body">
          <div className="s3-nov__bar">
            <span className="s3-nov__count">{voci.length === 1 ? '1 novità per te' : `${voci.length} novità per te`}</span>
            <button type="button" className="s3-nov__all" onClick={toggleTutte}>
              {tutteAperte ? 'Comprimi tutte' : 'Espandi tutte'}
            </button>
          </div>
          <ul className="s3-nov__list">
            {voci.map((v) => {
              const Icona = ICONE[v.icona] || Sparkles;
              const espansa = aperte.has(v.id);
              const detId = `s3-nov-det-${v.id}`;
              return (
                <li key={v.id} className={`s3-nov__item${espansa ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="s3-nov__head"
                    aria-expanded={espansa}
                    aria-controls={detId}
                    onClick={() => toggle(v.id)}
                  >
                    <span className={`s3-nov__ico s3-nov__ico--${v.icona}`} aria-hidden="true"><Icona size={16} /></span>
                    <span className="s3-nov__txt">
                      <span className="s3-nov__t">{v.titolo}</span>
                      <span className="s3-nov__s">{v.sintesi}</span>
                    </span>
                    <ChevronDown size={16} className="s3-nov__chev" aria-hidden="true" />
                  </button>
                  {/* Sempre nel DOM (hidden da compressa): aria-controls punta a un id esistente. */}
                  <div className="s3-nov__det" id={detId} hidden={!espansa}>
                    {v.dettaglio.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="s3-mod__foot s3-nov__foot">
          <div className="s3-nov__links">
            <a href={NOVITA_VIDEO_URL} target="_blank" rel="noopener noreferrer" className="s3-nov__link">
              <CirclePlay size={14} aria-hidden="true" />Guarda il video guida
            </a>
            {/* Rispetta la spunta: chi ha scelto «Non mostrare più» e apre la guida la salva. */}
            <Link to={guida} className="s3-nov__link" onClick={() => onClose(nonMostrare)}>
              <BookOpen size={14} aria-hidden="true" />Guida all’uso
            </Link>
          </div>
          <div className="s3-nov__actions">
            <label className="s3-nov__check">
              <input type="checkbox" checked={nonMostrare} onChange={(e) => setNonMostrare(e.target.checked)} />
              Non mostrare più
            </label>
            <button type="button" className="s3-pill s3-pill--primary" data-autofocus onClick={() => onClose(nonMostrare)}>
              Ho capito
            </button>
          </div>
        </div>
      </div>
    </S3Modal>
  );
};

export default NovitaModal;
